import { alpha, createTheme, Theme } from '@mui/material/styles';
import type { } from '@mui/lab/themeAugmentation';
import { Handshake, LocalShipping } from '@mui/icons-material';
import { ReactComponent as AllabolagIcon } from '@icons/allabolag-icon.svg';
import { ReactComponent as FairTransportIcon } from '@icons/fairtransport-icon.svg';
import { ReactComponent as LogicLinkIcon } from '@icons/logiclink-icon.svg';

/**
 * Resolves a color from the theme palette or returns the color directly if it's a custom color code.
 * @param theme - The Material-UI theme object.
 * @param color - The color key (e.g., 'success', 'info') or a custom color code (e.g., '#ff0000').
 * @returns The resolved color value.
 */
export const resolveThemeColor = (theme: Theme, color?: string): string | undefined => {
    if (!color) return undefined;

    const paletteEntry = theme.palette[color as keyof typeof theme.palette];
    return paletteEntry && typeof paletteEntry === 'object' && 'main' in paletteEntry
        ? paletteEntry.main
        : color;
};

// ---------------------------------------------------------------------------
// Brand — grafisk profil v1.0
// ---------------------------------------------------------------------------

// Indigo
export const COLOR_INDIGO = '#1D2570';

// Navy (mörkblå)
export const COLOR_BLUE = '#0E2A43';
export const COLOR_NEW_BLUE = COLOR_BLUE;
export const COLOR_OUTLINE = '#6E7F8E'; // navy 60 %

// Petrol
export const COLOR_TEAL = '#006D77';
export const COLOR_TEAL_SURFACE = '#E4EFF0';
export const COLOR_SURFACE = COLOR_TEAL_SURFACE;

// Mint
export const COLOR_MINT = '#7DCFA5';
export const COLOR_MINT_LIGHT = '#B1E2C9'; // mint 60 %
export const COLOR_GREEN_LIGHT = COLOR_MINT_LIGHT;

// Slate / neutrals
export const COLOR_SLATE_LIGHT = '#9EAAB7'; // skiffer 60 %
export const COLOR_GREY = COLOR_SLATE_LIGHT;
export const COLOR_GREY_DARK = '#555555';
export const COLOR_GREY_TRANSLUCENT = alpha(COLOR_GREY, 0.1);
export const COLOR_WHITEISH = '#EFF5F6'; // ljus petrol 60 %
export const COLOR_WHITE = '#FFFFFF';
export const COLOR_NAV_HOVER = 'rgba(255, 255, 255, 0.10)';
export const COLOR_NAV_ACTIVE = 'rgba(255, 255, 255, 0.18)';

// ---------------------------------------------------------------------------
// Domain (karta, status, entity — inte varumärke)
// ---------------------------------------------------------------------------

export const COLOR_PURPLE = '#522653';
export const COLOR_PURPLE_LIGHT = '#795679';
export const COLOR_YELLOW = '#FDB827';
export const COLOR_RED = '#FF0000';

// ---------------------------------------------------------------------------
// Gradients (Premium)
// ---------------------------------------------------------------------------

export const COLOR_BLUE_GRAD_MID = '#215893';
export const COLOR_BLUE_GRAD_END_HEX = '#2867AB';
export const COLOR_BLUE_GRAD_START = alpha(COLOR_BLUE, 0.9);
export const COLOR_BLUE_GRAD_MIDDLE = alpha(COLOR_BLUE_GRAD_MID, 0.9);
export const COLOR_BLUE_GRAD_END = alpha(COLOR_BLUE_GRAD_END_HEX, 0.9);
export const COLOR_BLUE_GRADIENT = `linear-gradient(135deg, ${COLOR_BLUE_GRAD_START} 0%, ${COLOR_BLUE_GRAD_MIDDLE} 65%, ${COLOR_BLUE_GRAD_END} 100%)`;

export const COLOR_GRAD_MIDDLE_HEX = '#F4EAEA';
export const COLOR_WHITE_GRAD_END_HEX = '#E9E1E1';
export const COLOR_WHITE_GRAD_START = alpha(COLOR_WHITEISH, 0.16);
export const COLOR_GRAD_MIDDLE = alpha(COLOR_GRAD_MIDDLE_HEX, 0.1);
export const COLOR_WHITE_GRAD_END = alpha(COLOR_WHITE_GRAD_END_HEX, 0.05);
export const COLOR_WHITE_GRADIENT = `linear-gradient(135deg, ${COLOR_WHITE_GRAD_START} 0%, ${COLOR_GRAD_MIDDLE} 50%, ${COLOR_WHITE_GRAD_END} 100%)`;

export const COLOR_GREEN_GRAD_START = alpha(COLOR_MINT_LIGHT, 0.1);
export const COLOR_GREEN_GRAD_MIDDLE = alpha(COLOR_MINT, 0.05);
export const COLOR_GREEN_GRAD_END = alpha(COLOR_MINT_LIGHT, 0.05);
export const COLOR_GREEN_GRADIENT = `linear-gradient(135deg, ${COLOR_GREEN_GRAD_START} 0%, ${COLOR_GREEN_GRAD_MIDDLE} 50%, ${COLOR_GREEN_GRAD_END} 100%)`;

export const colors = {
    blue: COLOR_BLUE,
    blueGradient: COLOR_BLUE_GRADIENT,
    greenLight: COLOR_GREEN_LIGHT,
    greenGradient: COLOR_GREEN_GRADIENT,
    greyTransluscent: COLOR_GREY_TRANSLUCENT,
    mint: COLOR_MINT,
    tealSurface: COLOR_TEAL_SURFACE,
    white: COLOR_WHITE,
    whiteGradient: COLOR_WHITE_GRADIENT
} as const;

// ---------------------------------------------------------------------------
// Icons, type, elevation
// ---------------------------------------------------------------------------

const CARRIER_ICON = LocalShipping;
const BUYER_ICON = Handshake;

export const icons = {
    carrier: CARRIER_ICON,
    buyer: BUYER_ICON,
    allabolag: AllabolagIcon,
    fairtransport: FairTransportIcon,
    logiclink: LogicLinkIcon
} as const;

export const FONT_FAMILY_SANS = [
    'Inter',
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Helvetica',
    'Arial',
    'sans-serif'
].join(',');

export const FONT_FAMILY_DISPLAY = [
    'Fraunces',
    'Georgia',
    'serif'
].join(',');

const MD_ELEV_1 = '0 1px 2px rgba(14,42,67,0.06), 0 1px 3px rgba(14,42,67,0.04)';
const MD_ELEV_2 = '0 2px 4px rgba(14,42,67,0.06), 0 4px 8px rgba(14,42,67,0.05)';
const MD_ELEV_3 = '0 4px 8px rgba(14,42,67,0.08), 0 8px 16px rgba(14,42,67,0.06)';

export function createAppTheme() {
    return createTheme({
        palette: {
            primary: {
                main: COLOR_BLUE,
                contrastText: COLOR_WHITE
            },
            secondary: {
                main: COLOR_MINT,
                contrastText: COLOR_BLUE
            },
            info: {
                main: COLOR_INDIGO
            },
            background: {
                default: COLOR_SURFACE,
                paper: COLOR_WHITE
            },
            job: {
                main: COLOR_TEAL,
                contrastText: COLOR_WHITE
            }
        },
        typography: {
            fontFamily: FONT_FAMILY_SANS,
            button: {
                fontFamily: FONT_FAMILY_SANS,
                fontWeight: 600,
                fontSize: 12,
                lineHeight: '16px',
                letterSpacing: '0.07em',
                textTransform: 'uppercase'
            }
        },
        shape: {
            borderRadius: 12
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    body: {
                        backgroundColor: COLOR_SURFACE
                    }
                }
            },
            MuiButton: {
                defaultProps: {
                    disableElevation: true
                },
                styleOverrides: {
                    root: {
                        borderRadius: 9999,
                        fontWeight: 600
                    },
                    sizeMedium: {
                        height: 40,
                        padding: '0 24px'
                    },
                    textSizeMedium: {
                        padding: '0 12px'
                    },
                    contained: {
                        '&:hover': {
                            boxShadow: MD_ELEV_1
                        }
                    },
                    outlined: {
                        borderColor: COLOR_OUTLINE
                    }
                }
            },
            MuiLoadingButton: {
                defaultProps: {
                    disableElevation: true
                }
            },
            MuiPaper: {
                styleOverrides: {
                    rounded: {
                        borderRadius: 12
                    },
                    elevation1: { boxShadow: MD_ELEV_1 },
                    elevation2: { boxShadow: MD_ELEV_2 },
                    elevation3: { boxShadow: MD_ELEV_2 }
                }
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        borderRadius: 8
                    }
                }
            },
            MuiChip: {
                styleOverrides: {
                    root: {
                        height: 32,
                        borderRadius: 8,
                        fontWeight: 500,
                        letterSpacing: '0.1px'
                    }
                }
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        borderColor: alpha(COLOR_BLUE, 0.08),
                        padding: '14px 16px'
                    },
                    head: {
                        fontWeight: 600,
                        fontSize: 13,
                        letterSpacing: '0.01em',
                        textTransform: 'none',
                        color: COLOR_BLUE,
                        backgroundColor: COLOR_GREY_TRANSLUCENT
                    }
                }
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        '&:last-child td, &:last-child th': {
                            borderBottom: 0
                        }
                    }
                }
            },
            MuiSnackbarContent: {
                styleOverrides: {
                    root: {
                        borderRadius: 12,
                        boxShadow: MD_ELEV_1
                    }
                }
            },
            MuiDialog: {
                styleOverrides: {
                    paper: ({ theme }) => ({
                        borderRadius: 12,
                        boxShadow: MD_ELEV_3,
                        [theme.breakpoints.down('sm')]: {
                            width: 'calc(100% - 32px)',
                            maxWidth: 'calc(100% - 32px)',
                            margin: 16
                        }
                    })
                }
            }
        }
    });
}
